import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThunderboltOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  CloseOutlined,
  CalendarOutlined,
  FlagOutlined,
  PercentageOutlined,
  FileTextOutlined,
  AimOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import {
  message,
  Card,
  Form,
  Input,
  Button,
  Select,
  Slider,
  Radio,
  Row,
  Col,
  Space,
  Typography,
  Divider,
  Alert,
  Statistic,
  Progress,
  Tag,
} from 'antd';
import PersianDatePicker from '../../components/PersianDatePicker';
import { toGregorian, isValidDate, isAfter } from '../../utils/dateUtils';
import { actionApi, ActionValidationError } from '../../utils/actionApi';
import moment from 'moment-jalaali';
import { getSuccessMessage } from '../../utils/actionMessageTranslator';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CreateAction = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [percentComplete, setPercentComplete] = useState(0);

  // مقادیر معتبر مطابق بک‌اند
  const actionTypes = [
    { value: 'اصلاح', label: 'اصلاح' },
    { value: 'اقدام_پیشگیرانه', label: 'اقدام پیشگیرانه' },
    { value: 'اقدام_اصلاحی', label: 'اقدام اصلاحی' },
  ];

  const statuses = [
    { value: 'باز', label: 'باز', color: 'processing' },
    { value: 'در_حال_برنامه_ریزی', label: 'در حال برنامه‌ریزی', color: 'warning' },
    { value: 'در_حال_انجام', label: 'در حال انجام', color: 'processing' },
    { value: 'بسته', label: 'بسته', color: 'success' },
  ];

  const priorities = [
    { value: 'فوری_و_اضطراری', label: 'فوری و اضطراری', color: 'red' },
    { value: 'بسیار_مهم', label: 'بسیار مهم', color: 'volcano' },
    { value: 'مهم', label: 'مهم', color: 'orange' },
    { value: 'کم_اهمیت', label: 'کم اهمیت', color: 'green' },
    { value: 'غیرضروری', label: 'غیرضروری', color: 'default' },
  ];

  // استفاده از utility مرکزی برای تبدیل تاریخ
  const formatDate = (val) => {
    if (!val) return null;
    
    // اگر DateObject از react-multi-date-picker باشد (که شامل ساعت است)
    if (val?.toDate && typeof val.toDate === 'function') {
      const dateObj = val.toDate();
      const m = moment(dateObj);
      if (m && m.isValid()) {
        // بررسی اینکه آیا ساعت هم دارد یا نه
        const hasTime = val.hour !== undefined || val.minute !== undefined || 
                        (dateObj.getHours() !== 0 || dateObj.getMinutes() !== 0 || dateObj.getSeconds() !== 0);
        if (hasTime) {
          // تبدیل به فرمت میلادی با ساعت برای API: "2025-12-28 14:00:00"
          return m.format('YYYY-MM-DD HH:mm:ss');
        }
        // بدون ساعت: فقط تاریخ میلادی
        return m.format('YYYY-MM-DD');
      }
    }
    
    // اگر رشته شامل ساعت است (مثلاً "1403/10/15 14:30" یا "1403/10/15 | 14:00")
    if (typeof val === 'string') {
      let datePart, timePart;
      
      if (val.includes(' | ')) {
        const parts = val.split(' | ');
        datePart = parts[0];
        timePart = parts[1] || '';
      } else if (val.includes(' ')) {
        const parts = val.split(' ');
        datePart = parts[0];
        timePart = parts[1] || '';
      } else {
        datePart = val;
        timePart = '';
      }
      
      // تبدیل تاریخ جلالی به میلادی
      const gregorianDate = toGregorian(datePart, 'YYYY-MM-DD');
      if (!gregorianDate) return null;
      
      if (timePart) {
        const timeParts = timePart.split(':');
        if (timeParts.length === 2) {
          return `${gregorianDate} ${timePart}:00`;
        }
        return `${gregorianDate} ${timePart}`;
      }
      
      return gregorianDate;
    }
    
    // برای سایر موارد، از toGregorian استفاده می‌کنیم
    return toGregorian(val, 'YYYY-MM-DD');
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      // تبدیل تاریخ‌های جلالی به میلادی
      const payload = {
        ...values,
        planned_start_date: formatDate(values.planned_start_date),
        planned_end_date: formatDate(values.planned_end_date),
        // actual_start_date: formatDate(values.actual_start_date),
        // actual_end_date: formatDate(values.actual_end_date),
        percent_complete: typeof values.percent_complete === 'number' ? values.percent_complete : percentComplete,
      };

      // حذف فیلدهای خالی
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      // تبدیل عددی فیلدها
      if (payload.weight !== undefined) payload.weight = Number(payload.weight);
      if (payload.source_id !== undefined) payload.source_id = Number(payload.source_id);
      if (payload.percent_complete !== undefined) payload.percent_complete = Number(payload.percent_complete);

      // ارسال به API
      const result = await actionApi.createAction(payload);
      
      if (result.success) {
        message.success(getSuccessMessage(result.message, 'اقدام با موفقیت ایجاد شد'));
        setTimeout(() => navigate('/action/my-actions'), 1000);
      } else {
        message.error(result.message || 'خطا در ایجاد اقدام');
      }
    } catch (error) {
      console.error('Create action error:', error);
      
      // هندل خطاهای 422 (Validation)
      if (error instanceof ActionValidationError) {
        // تبدیل خطاها به فرمت Ant Design Form
        const fieldsError = [];
        
        Object.keys(error.errors).forEach(fieldName => {
          const fieldErrors = Array.isArray(error.errors[fieldName]) 
            ? error.errors[fieldName] 
            : [error.errors[fieldName]];
          
          // تبدیل نام فیلد به نام فارسی (اختیاری)
          const fieldTranslations = {
            'priority': 'اولویت',
            'action_type': 'نوع اقدام',
            'status': 'وضعیت',
            'title': 'عنوان',
            'description': 'توضیحات',
            'objective': 'هدف',
            'percent_complete': 'درصد پیشرفت',
            'weight': 'وزن',
            'planned_start_date': 'تاریخ شروع برنامه‌ریزی‌شده',
            'planned_end_date': 'تاریخ پایان برنامه‌ریزی‌شده',
            // 'actual_start_date': 'تاریخ شروع واقعی',
            // 'actual_end_date': 'تاریخ پایان واقعی',
            'source_module': 'ماژول منبع',
            'source_id': 'شناسه منبع',
          };
          
          const fieldLabel = fieldTranslations[fieldName] || fieldName;
          
          fieldsError.push({
            name: fieldName,
            errors: fieldErrors.map(err => `${fieldLabel}: ${err}`),
          });
        });
        
        // نمایش خطاها زیر فیلدهای مربوطه
        form.setFields(fieldsError);
        
        // نمایش پیام کلی
        message.error(error.message || 'لطفاً خطاهای فرم را برطرف کنید');
      } else {
        message.error(error.message || 'خطا در ایجاد اقدام');
      }
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = priorities.map(priority => ({
    label: (
      <Space>
        <Tag color={priority.color}>
          {priority.label}
        </Tag>
      </Space>
    ),
    value: priority.value,
  }));

  const statusOptions = statuses.map(status => ({
    label: (
      <Space>
        <Tag color={status.color}>
          {status.label}
        </Tag>
      </Space>
    ),
    value: status.value,
  }));

  return (
    <MainLayout>
      <div style={{ padding: '24px', width: '100%' }}>
        {/* هدر */}
        <Card
          bordered={false}
          style={{ marginBottom: 24, borderRadius: 16 }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: 24 }}>
            <Space direction="horizontal" size="middle" align="center" style={{ width: '100%', marginBottom: 16 }}>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/action/dashboard')}
                style={{ fontFamily: 'CustomFont', borderRadius: 12 }}
              >
                بازگشت
              </Button>
              <Space size="small">
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 16,
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ThunderboltOutlined style={{ fontSize: 20, color: 'white' }} />
                </div>
                <div>
                  <Title level={3} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                    ساخت اقدام جدید
                  </Title>
                  <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                    اطلاعات اقدام را با دقت وارد کنید
                  </Text>
                </div>
              </Space>
            </Space>
          </div>
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          style={{ fontFamily: 'CustomFont' }}
        >
          {/* اطلاعات اصلی */}
          <Card
            title={
              <Space>
                <FileTextOutlined style={{ color: '#10b981' }} />
                <span style={{ fontFamily: 'CustomFont' }}>اطلاعات اصلی</span>
              </Space>
            }
            bordered={false}
            style={{ marginBottom: 24, borderRadius: 16 }}
            headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
            bodyStyle={{ padding: 24 }}
          >
            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label="عنوان اقدام"
                  name="title"
                  rules={[
                    { required: true, message: 'لطفا عنوان اقدام را وارد کنید' },
                    { max: 255, message: 'حداکثر ۲۵۵ کاراکتر مجاز است' },
                  ]}
                >
                  <Input 
                    size="large" 
                    placeholder="عنوان واضح و مشخص برای اقدام وارد کنید..." 
                    style={{ borderRadius: 12 }}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="توضیحات"
                  name="description"
                >
                  <TextArea 
                    rows={4} 
                    placeholder="توضیحات کامل در مورد اقدام..." 
                    style={{ borderRadius: 12 }}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label={
                    <Space>
                      <AimOutlined style={{ color: '#10b981' }} />
                      <span>هدف از اقدام</span>
                    </Space>
                  }
                  name="objective"
                >
                  <Input 
                    size="large" 
                    placeholder="هدف نهایی این اقدام چیست؟" 
                    style={{ borderRadius: 12 }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* دسته‌بندی و وضعیت */}
          <Card
            title={
              <Space>
                <FlagOutlined style={{ color: '#10b981' }} />
                <span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی و وضعیت</span>
              </Space>
            }
            bordered={false}
            style={{ marginBottom: 24, borderRadius: 16 }}
            headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
            bodyStyle={{ padding: 24 }}
          >
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Form.Item
                  label="اولویت"
                  name="priority"
                  rules={[{ required: true, message: 'لطفا اولویت را انتخاب کنید' }]}
                >
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    size="large"
                    style={{ width: '100%', display: 'flex', gap: '8px' }}
                  >
                    {priorities.map(priority => (
                      <Radio.Button
                        key={priority.value}
                        value={priority.value}
                        style={{
                          flex: 1,
                          textAlign: 'center',
                          borderRadius: 12,
                        }}
                      >
                        <Tag color={priority.color} style={{ border: 'none' }}>
                          {priority.label}
                        </Tag>
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="نوع اقدام"
                      name="action_type"
                      rules={[{ required: true, message: 'لطفا نوع اقدام را انتخاب کنید' }]}
                    >
                      <Select
                        size="large"
                        placeholder="نوع اقدام را انتخاب کنید"
                        style={{ borderRadius: 12 }}
                      >
                        {actionTypes.map(type => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label="وضعیت"
                      name="status"
                      rules={[{ required: true, message: 'لطفا وضعیت را انتخاب کنید' }]}
                    >
                      <Select
                        size="large"
                        placeholder="وضعیت را انتخاب کنید"
                        style={{ borderRadius: 12 }}
                        options={statusOptions}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="ماژول منبع"
                      name="source_module"
                      rules={[
                        { max: 255, message: 'حداکثر ۲۵۵ کاراکتر مجاز است' },
                      ]}
                    >
                      <Select
                        size="large"
                        placeholder="ماژول منبع را انتخاب کنید"
                        style={{ borderRadius: 12 }}
                        allowClear
                      >
                        <Option value="crm">CRM</Option>
                        <Option value="erp">ERP</Option>
                        <Option value="hr">HR</Option>
                        <Option value="inventory">Inventory</Option>
                        <Option value="finance">Finance</Option>
                        <Option value="project">Project</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  {/* فیلد شناسه منبع موقتاً غیرفعال شده است */}
                  {/* <Col xs={24} md={12}>
                    <Form.Item
                      label="شناسه منبع"
                      name="source_id"
                    >
                      <Input
                        type="number"
                        size="large"
                        placeholder="شناسه مرتبط در سیستم منبع"
                        style={{ borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col> */}
                </Row>
              </Col>
            </Row>
          </Card>

          {/* پیشرفت و وزن */}
          <Card
            title={
              <Space>
                <PercentageOutlined style={{ color: '#10b981' }} />
                <span style={{ fontFamily: 'CustomFont' }}>پیشرفت و اهمیت</span>
              </Space>
            }
            bordered={false}
            style={{ marginBottom: 24, borderRadius: 16 }}
            headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
            bodyStyle={{ padding: 24 }}
          >
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Form.Item
                  label={
                    <Space>
                      <span>درصد پیشرفت</span>
                      <Tag color="success" style={{ fontFamily: 'CustomFont' }}>
                        {percentComplete}%
                      </Tag>
                    </Space>
                  }
                  name="percent_complete"
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Slider
                      min={0}
                      max={100}
                      step={5}
                      value={percentComplete}
                      onChange={(value) => {
                        setPercentComplete(value);
                        form.setFieldsValue({ percent_complete: value });
                      }}
                      tooltip={{ formatter: (value) => `${value}%` }}
                    />
                    <Progress
                      percent={percentComplete}
                      strokeColor={{
                        '0%': '#34d399',
                        '100%': '#10b981',
                      }}
                      showInfo={false}
                    />
                  </Space>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="وزن اقدام (0 تا 100)"
                  name="weight"
                  rules={[
                    { min: 0, message: 'وزن نمی‌تواند منفی باشد' },
                    { max: 100, message: 'حداکثر وزن 100 است' }
                  ]}
                >
                  <Input
                    type="number"
                    size="large"
                    placeholder="اهمیت نسبی این اقدام را مشخص کنید"
                    min={0}
                    max={100}
                    style={{ borderRadius: 12 }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* تاریخ‌ها */}
          <Card
            title={
              <Space>
                <CalendarOutlined style={{ color: '#10b981' }} />
                <span style={{ fontFamily: 'CustomFont' }}>برنامه‌ریزی زمانی</span>
              </Space>
            }
            bordered={false}
            style={{ marginBottom: 24, borderRadius: 16 }}
            headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
            bodyStyle={{ padding: 24 }}
          >
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Title level={5} style={{ fontFamily: 'CustomFont', marginBottom: 16 }}>
                  تاریخ‌های برنامه‌ریزی‌شده
                </Title>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="تاریخ شروع"
                      name="planned_start_date"
                    >
                      <PersianDatePicker
                        placeholder="انتخاب تاریخ و ساعت شروع"
                        showTime={true}
                        style={{ width: '100%', height: '40px', borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="تاریخ پایان"
                      name="planned_end_date"
                  dependencies={['planned_start_date']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue('planned_start_date');
                        if (!start || !value) return Promise.resolve();
                        
                        // تبدیل DateObject به moment برای مقایسه
                        let startMoment, endMoment;
                        
                        if (start?.toDate && typeof start.toDate === 'function') {
                          startMoment = moment(start.toDate());
                        } else if (typeof start === 'string') {
                          const startDatePart = start.split(' ')[0].split(' | ')[0];
                          startMoment = moment(startDatePart, 'jYYYY/jMM/jDD');
                        } else {
                          startMoment = moment(start);
                        }
                        
                        if (value?.toDate && typeof value.toDate === 'function') {
                          endMoment = moment(value.toDate());
                        } else if (typeof value === 'string') {
                          const endDatePart = value.split(' ')[0].split(' | ')[0];
                          endMoment = moment(endDatePart, 'jYYYY/jMM/jDD');
                        } else {
                          endMoment = moment(value);
                        }
                        
                        if (!startMoment || !startMoment.isValid() || !endMoment || !endMoment.isValid()) {
                          return Promise.resolve();
                        }
                        
                        const ok = endMoment.isAfter(startMoment) || endMoment.isSame(startMoment, 'day');
                        return ok ? Promise.resolve() : Promise.reject(new Error('تاریخ پایان باید بعد از تاریخ شروع باشد'));
                      },
                    }),
                  ]}
                    >
                      <PersianDatePicker
                        placeholder="انتخاب تاریخ و ساعت پایان"
                        showTime={true}
                        style={{ width: '100%', height: '40px', borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              {/* <Col span={24}>
                <Title level={5} style={{ fontFamily: 'CustomFont', marginBottom: 16 }}>
                  تاریخ‌های واقعی
                </Title>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="تاریخ شروع واقعی"
                      name="actual_start_date"
                    >
                      <PersianDatePicker
                        placeholder="انتخاب تاریخ و ساعت شروع واقعی"
                        showTime={true}
                        style={{ width: '100%', height: '40px', borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="تاریخ پایان واقعی"
                      name="actual_end_date"
                      dependencies={['actual_start_date']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const start = getFieldValue('actual_start_date');
                            if (!start || !value) return Promise.resolve();
                            const startIso = formatDate(start);
                            const endIso = formatDate(value);
                            if (!startIso || !endIso) return Promise.resolve();
                            const ok = isAfter(endIso, startIso) || endIso === startIso;
                            return ok ? Promise.resolve() : Promise.reject(new Error('تاریخ پایان باید بعد از تاریخ شروع باشد'));
                          },
                        }),
                      ]}
                    >
                      <PersianDatePicker
                        placeholder="انتخاب تاریخ و ساعت پایان واقعی"
                        showTime={true}
                        style={{ width: '100%', height: '40px', borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col> */}

            </Row>
          </Card>

          {/* توضیحات تکمیلی */}
          <Card
            title={
              <span style={{ fontFamily: 'CustomFont' }}>توضیحات تکمیلی</span>
            }
            bordered={false}
            style={{ marginBottom: 24, borderRadius: 16 }}
            headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
            bodyStyle={{ padding: 24 }}
          >
            <Form.Item name="extra_description">
              <TextArea
                rows={5}
                placeholder="هر توضیح اضافی که لازم است در اینجا بنویسید..."
                style={{ borderRadius: 12 }}
              />
            </Form.Item>
          </Card>

          {/* دکمه‌های عملیات */}
          <Card
            bordered={false}
            style={{ borderRadius: 16 }}
            bodyStyle={{ padding: 16 }}
          >
            <Space
              direction="horizontal"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
              }}
            >
              <Button
                type="default"
                size="large"
                icon={<CloseOutlined />}
                onClick={() => navigate('/action')}
                style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
              >
                انصراف
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={loading}
                style={{
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  fontFamily: 'CustomFont',
                }}
              >
                {loading ? 'در حال ذخیره...' : 'ذخیره اقدام'}
              </Button>
            </Space>
          </Card>
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateAction;